import { useCallback, useEffect, useMemo, useState } from "react";

import { decodeJwtClaims, ppUserIdFromClaims } from "../auth/jwt";
import {
  ClaimProviderListError,
  fetchClaimReadWriteProviderList,
  filterAndSortClaimProviders,
  type ClaimProvider,
} from "./claimProviderList";

/**
 * Loads the acting user's claim-write provider panel for the Create Claim entry
 * gate. FAILS CLOSED in every branch: the only status that lets the user
 * proceed is `"ready"`, which requires a real bearer, a real userId decoded
 * from THAT bearer, an OK response, and at least one provider in the acting IPA.
 *
 * Fails closed because the SEED endpoint (`GET ClaimMember/CreateClaim`) checks
 * no entitlement of its own, so nothing else stops a form from opening. Note this
 * is not the last authorization before a claim is filed — `SaveClaim` re-binds the
 * billing provider to the caller's own grants server-side (see
 * claimProviderList.ts's header). Both checks exist on purpose.
 *
 * `"no-identity"` / `"no-tenant"` / `"forbidden"` / `"unavailable"` / `"empty"`
 * all block. The endpoint is not deployed to dev yet, so `"unavailable"` (404)
 * is the expected state today — that is deliberate: Create Claim being
 * unavailable until the BE lands is correct, and much better than opening a form
 * whose every request 400s.
 */
export type ClaimProviderListStatus =
  /** Fetch in flight (or the bearer is still being minted). */
  | "loading"
  /** ≥1 provider with claim-write access in the acting IPA. The only pass. */
  | "ready"
  /** 200 OK, but no provider in this IPA grants claim write access. */
  | "empty"
  /** No PP bearer, or no userId decodable from it — nothing can be authorized. */
  | "no-identity"
  /**
   * No tenant/IPA in scope for this member at all (a Pathways member whose
   * `ipaCode` is `"NA"`, or an `astrana:`-only member carrying no companyId).
   *
   * DISTINCT from `"no-identity"` on purpose. Both block, but they have opposite
   * remedies and reporting a missing tenant as "Provider Portal sign-in
   * required" is a wrong diagnosis that reliably generates "but I AM signed in"
   * support tickets. Nothing the USER can do fixes this one — it is a property of
   * the member record.
   */
  | "no-tenant"
  /** HTTP 403 — the bearer's identity doesn't match `userId`, or no entitlement. */
  | "forbidden"
  /** 404 (endpoint not deployed) / 5xx / network / unusable body. */
  | "unavailable";

export interface UseClaimProviderListResult {
  status: ClaimProviderListStatus;
  /** Filtered to the acting IPA and sorted, exactly as PP's picker does. */
  providers: ClaimProvider[];
  /** HTTP status behind `forbidden`/`unavailable`, for the block copy. */
  httpStatus?: number;
  reload: () => void;
}

export interface UseClaimProviderListArgs {
  /**
   * PP API base URL INCLUDING `/api` — the same string the CreateClaim module
   * receives as `ppApiBaseUrl`.
   */
  ppApiBaseUrl: string;
  /**
   * The host's async PP-bearer getter (`stableGetClaimAccessToken`). Async so a
   * bearer that expired while the profile sat open is silently re-minted before
   * the gate decides anything. Returns `""` when no bearer can be obtained.
   *
   * The `userId` sent to PP-API is decoded from the RETURNED token inside this
   * hook, never passed in separately: PP-API compares the request's `userId`
   * against the bearer's own identity and answers 403 on any mismatch, so the
   * two must always move together (the same invariant MemberProfilePage
   * documents for `claimBearer`/`claimUserId`).
   */
  getAccessToken: () => string | Promise<string>;
  /**
   * Acting IPA — scopes the grant lookup AND filters the returned rows.
   *
   * `undefined` short-circuits to `"no-tenant"` and fires NO request. Callers use
   * that deliberately: the gate passes `undefined` while the member's Provider
   * Portal row is still unresolved, and once they are blocked on their health
   * plan, so no PP-API round trip is spent on a member who cannot proceed
   * regardless of their grants. A caller doing that MUST NOT surface the
   * resulting `"no-tenant"` as the reason — it is a suppression sentinel there,
   * not a diagnosis (see CreateClaimGate's body/telemetry ordering).
   */
  companyId: string | undefined;
  /**
   * Forwarded verbatim to `fetchClaimReadWriteProviderList` — drives the BE's
   * server-side role expansion (`SHOW_ALL_PROVIDERS_ROLES`). DELIBERATELY
   * OPTIONAL: a caller with no resolvable role still runs the fetch on its
   * explicit grants alone. Never gate/short-circuit on this being present —
   * only `companyId` (see above) is allowed to suppress the request.
   */
  userRole?: string;
}

/**
 * True for the rejection `fetch` produces on an aborted request.
 *
 * Duck-typed on `name` alone — deliberately NOT `e instanceof DOMException` and
 * NOT `e instanceof Error`. `DOMException` is not required to sit on Error's
 * prototype chain (verified: under jsdom `new DOMException(…) instanceof Error`
 * is FALSE), and `instanceof` fails across realms anyway, so either narrowing
 * would silently let a real abort through as a PP-API failure.
 */
function isAbortError(e: unknown): boolean {
  return (
    typeof e === "object" && e !== null && (e as { name?: unknown }).name === "AbortError"
  );
}

export function useClaimProviderList({
  ppApiBaseUrl,
  getAccessToken,
  companyId,
  userRole,
}: UseClaimProviderListArgs): UseClaimProviderListResult {
  const [status, setStatus] = useState<ClaimProviderListStatus>("loading");
  const [raw, setRaw] = useState<ClaimProvider[]>([]);
  const [httpStatus, setHttpStatus] = useState<number | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  // A network fetch is genuine external-system synchronization — the one case
  // this repo's anti-useEffect guidance allows, and the same shape
  // useActingUserIpa/useWorklistAccessGate use for their PP-API calls (there is
  // no query-hook layer for host-side direct PP-API calls to route through).
  useEffect(() => {
    let cancelled = false;
    const abort = new AbortController();
    setStatus("loading");
    setHttpStatus(undefined);
    setRaw([]);

    if (!companyId) {
      // No tenant context at all — a Pathways member whose ipaCode is "NA", or
      // an astrana:-only member. Also the deliberate signal the gate uses to
      // SUPPRESS this fetch entirely when the member is already blocked on their
      // health plan (it passes `companyId: undefined` then), so no PP-API round
      // trip is spent on a member who can't proceed anyway.
      //
      // Reported as its own status, never as "no-identity": the two have
      // opposite remedies (see ClaimProviderListStatus).
      setStatus("no-tenant");
      return;
    }

    void (async () => {
      let bearer = "";
      try {
        bearer = (await getAccessToken()) || "";
      } catch (e) {
        // Never swallow this. A throw here is indistinguishable from "no PP
        // session" in the UI, and the gate reports both as `no-identity` —
        // whose remedy ("sign in to Provider Portal") is wrong for a mint that
        // blew up while the user was already signed in.
        console.error("[claims] PP bearer mint threw while resolving claim access:", e);
        bearer = "";
      }
      if (cancelled) return;
      const userId = bearer ? ppUserIdFromClaims(decodeJwtClaims(bearer)) : undefined;
      if (!bearer || !userId) {
        // `no-identity` covers TWO failures with opposite remedies, so say
        // which one out loud rather than leaving them indistinguishable:
        //   - no bearer at all      => genuinely no PP session; sign-in helps.
        //   - bearer but no userId  => the PP session IS there but carries no
        //     sub/oid/objectId claim, so signing in again can never help. That
        //     is a token/policy-shape problem, and reporting it as "sign in"
        //     sends an already-signed-in user round a loop that cannot resolve.
        if (!bearer) {
          console.error(
            "[claims] Create Claim blocked: no Provider Portal bearer available (getAccessToken returned empty).",
          );
        } else {
          console.error(
            "[claims] Create Claim blocked: PP bearer present but carries NO user-id claim " +
              "(expected sub / oid / objectId). Signing in again will not fix this. Decoded claim keys:",
            Object.keys(decodeJwtClaims(bearer) ?? {}),
          );
        }
        setStatus("no-identity");
        return;
      }
      try {
        const providers = await fetchClaimReadWriteProviderList({
          ppApiBaseUrl,
          bearer,
          userId,
          companyId,
          userRole,
          signal: abort.signal,
        });
        if (cancelled) return;
        setRaw(providers);
        setStatus(
          filterAndSortClaimProviders(providers, companyId).length > 0 ? "ready" : "empty",
        );
      } catch (e) {
        // An abort is teardown, not a PP-API failure: reporting it as
        // "unavailable" would flash a spurious block (and a spurious denial
        // telemetry event) on every close/retry/member-switch. Today the hook
        // owns the only controller and always sets `cancelled` before aborting,
        // so the first check alone suffices — the other two keep the guard true
        // to its intent if that ever stops holding (e.g. a caller-supplied
        // signal, which `FetchClaimProviderListArgs` already permits).
        if (cancelled || abort.signal.aborted || isAbortError(e)) return;
        const s = e instanceof ClaimProviderListError ? e.status : undefined;
        setHttpStatus(s);
        setStatus(s === 403 ? "forbidden" : "unavailable");
      }
    })();

    return () => {
      cancelled = true;
      abort.abort();
    };
  }, [ppApiBaseUrl, companyId, getAccessToken, userRole, attempt]);

  const providers = useMemo(
    () => (companyId ? filterAndSortClaimProviders(raw, companyId) : []),
    [raw, companyId],
  );

  return { status, providers, httpStatus, reload };
}
